import { type Proposal, type CalendarDate } from './types'

export function formatCurrency(cents: number, currency: string): string {
  const amount = cents / 100
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(amount)
}

export function formatDate(dateString: string, locale: string = 'pt-BR'): string {
  return new Date(dateString).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export function formatShortDate(dateString: string, locale: string = 'pt-BR'): string {
  return new Date(dateString).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric'
  })
}

export function getProposalStatus(proposal: Proposal): 'available' | 'pending' | 'confirmed' {
  if (proposal.status === 'accepted' && proposal.payments?.some(p => p.status === 'paid')) {
    return 'confirmed'
  }
  if (proposal.status === 'pending') {
    return 'pending'
  }
  return 'available'
}

export function buildCalendarDates(year: number, month: number, proposals: Proposal[]): CalendarDate[] {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDate = new Date(firstDay)
  startDate.setDate(startDate.getDate() - startDate.getDay())
  
  const dates: CalendarDate[] = []
  const currentDate = new Date(startDate)
  
  while (currentDate <= lastDay || currentDate.getDay() !== 0) {
    const dateString = currentDate.toISOString().split('T')[0]
    const dayProposals = proposals.filter(p => p.event_date === dateString)
    
    let status: 'available' | 'pending' | 'confirmed' = 'available'
    if (dayProposals.length > 0) {
      const hasConfirmed = dayProposals.some(p => getProposalStatus(p) === 'confirmed')
      const hasPending = dayProposals.some(p => getProposalStatus(p) === 'pending')
      status = hasConfirmed ? 'confirmed' : hasPending ? 'pending' : 'available'
    }
    
    dates.push({
      date: new Date(currentDate),
      proposals: dayProposals,
      status
    })
    
    currentDate.setDate(currentDate.getDate() + 1)
    
    if (dates.length > 42) break
  }
  
  return dates
}

export function getStatusColor(status: 'available' | 'pending' | 'confirmed'): string {
  switch (status) {
    case 'confirmed':
      return 'bg-green-500'
    case 'pending':
      return 'bg-yellow-500'
    default:
      return 'bg-gray-200'
  }
}

export function getStatusTextColor(status: 'available' | 'pending' | 'confirmed'): string {
  switch (status) {
    case 'confirmed':
      return 'text-green-700'
    case 'pending':
      return 'text-yellow-700'
    default:
      return 'text-gray-700'
  }
}

export function getProposalStatusBadgeColor(status: string): string {
  switch (status) {
    case 'accepted':
      return 'bg-transparent text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700/50'
    case 'pending':
      return 'bg-transparent text-yellow-800 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700/50'
    case 'cancelled':
      return 'bg-transparent text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700/50'
    case 'expired':
      return 'bg-transparent text-gray-700 border border-gray-300 dark:bg-gray-800/30 dark:text-gray-400 dark:border-gray-700/50'
    default:
      return 'bg-transparent text-gray-700 border border-gray-300 dark:bg-gray-800/30 dark:text-gray-400 dark:border-gray-700/50'
  }
}
